import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, ShoppingBag, ArrowUpRight, Snowflake, Heart, Zap } from "lucide-react";
import { toast } from "sonner";
import WhatsAppIcon from "./WhatsAppIcon";
import Rating from "./Rating";
import { buildProductMessage, whatsappLink } from "../whatsapp";
import useWishlist from "../useWishlist";

export default function ProductCard({ product, onAdd, onBuyNow, index, total }) {
  const [qty, setQty] = useState(1);
  const wishlist = useWishlist();
  const wished = wishlist.has(product.id);

  const handleAdd = (e) => {
    e.stopPropagation();
    onAdd(product, qty);
    toast.success(`Added ${qty} × ${product.name}`, {
      description: "Open your basket to checkout.",
    });
  };
  const handleBuy = (e) => {
    e.stopPropagation();
    onAdd(product, qty);
    if (onBuyNow) onBuyNow();
  };

  const discount = product.mrp && product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
    : 0;

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="group relative bg-boneSoft rounded-4xl overflow-hidden border border-ink/5 shadow-soft hover:shadow-hoverCard lift flex flex-col"
    >
      <Link
        to="/shop"
        data-testid={`product-view-${product.id}`}
        className="relative aspect-[4/5] overflow-hidden bg-sageMist text-left img-zoom block"
        aria-label={product.full_name}
      >
        <img
          src={product.image}
          alt={product.full_name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {product.tag && (
          <span className="absolute top-4 left-4 inline-flex items-center px-3 py-1.5 rounded-full bg-bone/95 text-ink text-[10px] uppercase tracking-[0.22em] font-medium backdrop-blur-md">
            {product.tag}
          </span>
        )}
        {discount > 0 && (
          <span className="absolute top-4 left-4 mt-[34px] inline-flex items-center px-2.5 py-1 rounded-full bg-coral text-bone text-[10px] uppercase tracking-[0.2em] font-medium">
            -{discount}%
          </span>
        )}

        {/* Wishlist heart */}
        <button
          data-testid={`wishlist-toggle-${product.id}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            wishlist.toggle(product.id);
            toast(wished ? "Removed from wishlist" : "Added to wishlist");
          }}
          className={`absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full backdrop-blur-md transition-all ${
            wished
              ? "bg-coral text-bone"
              : "bg-bone/95 text-ink hover:bg-coral hover:text-bone"
          }`}
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
        >
          <Heart className={`h-4 w-4 ${wished ? "fill-current" : ""}`} />
        </button>

        <span className="absolute bottom-4 left-4 font-italic text-bone text-[15px] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
      </Link>

      <div className="p-6 md:p-7 flex flex-col flex-grow gap-3.5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.24em] text-muted">
            {product.weight} · {product.category}
          </p>
          <h3 className="font-display text-[26px] md:text-[28px] mt-2 text-ink leading-[1.05] tracking-tightest">
            {product.full_name}
          </h3>
          {product.rating && (
            <div className="mt-2.5">
              <Rating value={product.rating} count={product.reviewCount} />
            </div>
          )}
        </div>

        <p className="text-sm text-ink/60 leading-relaxed line-clamp-2">
          {product.description}
        </p>

        <div className="flex items-center gap-2 text-xs text-muted">
          <Snowflake className="h-3.5 w-3.5 text-sageDeep" />
          <span className="line-clamp-1">{product.storage}</span>
        </div>

        <div className="mt-auto flex items-center justify-between pt-4 border-t border-ink/5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-muted">
              {product.tiers ? "Starts at" : "Price"}
            </p>
            <div className="flex items-baseline gap-2 mt-1">
              <p className="font-display text-[28px] text-ink leading-none tabular-nums tracking-tightest">
                ₹{product.price}
              </p>
              {product.mrp && product.mrp > product.price && (
                <span className="text-muted line-through text-sm tabular-nums">
                  ₹{product.mrp}
                </span>
              )}
              {product.tiers && (
                <span className="text-muted text-xs">/ kg</span>
              )}
            </div>
            {product.tiers && (
              <p className="text-[10px] text-coral mt-1 font-medium">
                Bulk: ₹{product.tiers[2].price}/kg @ 50+ kg
              </p>
            )}
          </div>

          <div
            className="flex items-center border border-ink/15 rounded-full bg-bone"
            data-testid={`qty-stepper-${product.id}`}
          >
            <button
              data-testid={`qty-dec-${product.id}`}
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="h-9 w-9 grid place-items-center text-ink/70 hover:text-coral"
              aria-label="Decrease"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span
              data-testid={`qty-value-${product.id}`}
              className="w-6 text-center text-sm tabular-nums"
            >
              {qty}
            </span>
            <button
              data-testid={`qty-inc-${product.id}`}
              onClick={() => setQty((q) => q + 1)}
              className="h-9 w-9 grid place-items-center text-ink/70 hover:text-coral"
              aria-label="Increase"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAdd}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-bone border border-ink/15 hover:border-ink text-ink transition-colors px-4 py-3 text-sm font-medium"
          >
            <ShoppingBag className="h-4 w-4" />
            Add
          </button>
          <button
            data-testid={`buy-now-${product.id}`}
            onClick={handleBuy}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-coral hover:bg-coralDeep text-bone transition-colors px-4 py-3 text-sm font-medium"
          >
            <Zap className="h-4 w-4" />
            Buy now
          </button>
        </div>

        <a
          data-testid={`whatsapp-order-${product.id}`}
          href={whatsappLink(buildProductMessage(product, qty))}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-whatsapp text-whatsapp hover:bg-whatsapp hover:text-white transition-colors px-4 py-2.5 text-sm font-medium"
        >
          <WhatsAppIcon className="h-4 w-4" />
          Order on WhatsApp
          <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </article>
  );
}
